import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { IconButton } from '@mui/material';
import { GridSortItem } from '@mui/x-data-grid/models/gridSortModel';
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridColumnMenu,
  GridColumnMenuProps,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import CustomPagination from './Pagination';

interface Props extends DataGridProProps {
  rows?: any;
  columns: GridColDef[];
  rowCount?: number;
  isLoading?: boolean;
  setSelectedRow?: Dispatch<SetStateAction<GridRowSelectionModel>>;
  pageNumber: number;
  setPageNumberChange: Dispatch<SetStateAction<number>>;
  setSorting?: Dispatch<SetStateAction<GridSortItem[]>>;
  checkboxSelection?: boolean;
  maxSelected?: number;
  selectedRow?: GridRowSelectionModel;
  disableMultipleRowSelection?: boolean;
  hideFooterPagination?: boolean;
  handleRowsSelection?: (newSelectedRow: any, rows: any[]) => void;
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        // Hide `columnMenuColumnsItem`
        columnMenuColumnsItem: null,
      }}
    />
  );
}

const DataGridProTable = (props: Props) => {
  const {
    rows,
    columns,
    rowCount,
    isLoading = false,
    setSelectedRow,
    pageNumber,
    setPageNumberChange,
    setSorting,
    maxSelected,
    selectedRow,
    checkboxSelection = true,
    hideFooterPagination,
    handleRowsSelection,
    sx,
    ...rest
  } = props;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [rowCounts, setRowCount] = useState(rowCount || 0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const apiRef = useGridApiRef();

  useEffect(() => {
    if (rowCount != undefined) {
      setRowCount(rowCount);
    }
  }, [rowCount]);

  const handlePaginationModalCHange = React.useCallback(
    (value: GridPaginationModel) => {
      setPaginationModel(value);
      setPageNumberChange(value?.page);
    },
    []
  );

  useEffect(() => {
    setPaginationModel({
      page: pageNumber,
      pageSize: 10,
    });
  }, [pageNumber]);

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      setSorting?.([...sortModel]);
    },
    [setSorting]
  );

  const handleRowSelectionChange = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      if (maxSelected && newRowSelectionModel.ids.size > maxSelected) {
        const selectionSet = new Set(selectedRow?.ids);
        const result = [...newRowSelectionModel.ids].filter(
          (s) => !selectionSet.has(s)
        );

        setSelectedRow?.({ ...newRowSelectionModel, ids: new Set(result) });
      } else {
        setSelectedRow?.(newRowSelectionModel);
        if (handleRowsSelection) {
          handleRowsSelection(newRowSelectionModel, rows);
        }
      }
    },
    [maxSelected]
  );

  const rowHeight = 53;
  const headerHeight = 56;
  const paginationHeight = 52;
  const totalRows = rows?.length > 10 ? 10 : rows?.length;
  const calculatedHeight =
    headerHeight + paginationHeight + totalRows * rowHeight;

  const scrollBy = (delta: number) => {
    const api = apiRef.current;
    if (!api) return;
    const position = api.getScrollPosition?.();
    if (!position) return;
    api.scroll({
      left: position.left + delta,
    });
  };

  const scrollLeft = () => scrollBy(-100);
  const scrollRight = () => scrollBy(100);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    let resizeObserver: ResizeObserver | null = null;
    const setupObserver = () => {
      const root = api.rootElementRef?.current;
      if (!root) return;
      const scroller = root.querySelector(
        '.MuiDataGrid-virtualScroller'
      ) as HTMLElement | null;
      if (!scroller) return;

      const update = () => {
        const { left } = api.getScrollPosition();
        setCanScrollLeft(left > 0);
        setCanScrollRight(
          scroller.scrollWidth > scroller.clientWidth &&
            left + scroller.clientWidth < scroller.scrollWidth
        );
      };
      update();
      // observe scrollWidth changes
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(scroller);
      // update on scroll
      const unsubscribe = api.subscribeEvent('scrollPositionChange', update);
      return unsubscribe;
    };
    // wait until grid finishes rendering
    const timeout = setTimeout(setupObserver, 0);
    return () => {
      clearTimeout(timeout);
      resizeObserver?.disconnect();
    };
  }, [columns, rows]);

  return (
    <div
      style={{
        width: '100%',
        height: `${calculatedHeight}px`,
        minHeight: '400px',
        position: 'relative',
      }}
    >
      {canScrollLeft && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            position: 'absolute',
            top: 17,
            left: -9,
            zIndex: 1,
            backgroundColor: '#fff',
            boxShadow: 2,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#d0d0d0',
            },
          }}
        >
          <ArrowBackIosNewIcon
            fontSize="small"
            sx={{ width: '13px', height: '13px' }}
          />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton
          onClick={scrollRight}
          sx={{
            position: 'absolute',
            top: 17,
            right: -9,
            zIndex: 1,
            backgroundColor: '#fff',
            boxShadow: 2,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#d0d0d0',
            },
          }}
        >
          <ArrowForwardIosIcon
            fontSize="small"
            sx={{ width: '13px', height: '13px' }}
          />
        </IconButton>
      )}
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={rows}
        columns={columns}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        rowCount={rowCounts || 0}
        loading={isLoading}
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handlePaginationModalCHange}
        disableColumnMenu
        hideFooterPagination={hideFooterPagination}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
          basePagination: {
            material: {
              ActionsComponent: (props: any) => (
                <CustomPagination {...props} isLoading={isLoading} />
              ),
            },
          },
        }}
        slots={{
          columnMenu: CustomColumnMenu,
        }}
        initialState={{
          ...rest.initialState,
          pagination: { paginationModel: paginationModel },
        }}
        onRowSelectionModelChange={handleRowSelectionChange}
        rowSelectionModel={selectedRow}
        sx={[
          {
            '.MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '&.MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'riPrimary.10',
              fontSize: 16,
              borderRadius: '50px',
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeaderRow:nth-child(1)': {
              borderRadius: '50px 0 0 50px',
            },
            '& .MuiDataGrid-columnHeader--pinnedLeft': {
              backgroundColor: 'riPrimary.10',
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        checkboxSelectionVisibleOnly
        {...rest}
      />
    </div>
  );
};

export default DataGridProTable;
